import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import api from '../utils/Api';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export default function WhatsNewDialog({ platform = 'admin-dashboard' }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [notes, setNotes] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  const activeNote = notes[activeIndex] || null;

  const canFetch = useCallback(
    () => Boolean(localStorage.getItem('token') && localStorage.getItem('user')),
    [],
  );

  useEffect(() => {
    let mounted = true;

    const loadNotes = async () => {
      if (!canFetch() || open) return;

      try {
        const res = await api.get('/release-notes/unseen', {
          params: { platform, limit: 5 },
        });
        const incoming = res.data?.notes || [];
        if (mounted && incoming.length) {
          setNotes(incoming);
          setActiveIndex(0);
          setOpen(true);
        }
      } catch (error) {
        console.warn('Failed to load what is new:', error.response?.data?.message || error.message);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotes();
      }
    };

    loadNotes();
    const intervalId = window.setInterval(loadNotes, 45000);
    window.addEventListener('focus', loadNotes);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', loadNotes);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canFetch, open, platform]);

  const markSeen = useCallback(async (noteId) => {
    if (!noteId) return;
    try {
      await api.post(`/release-notes/${noteId}/seen`);
    } catch (error) {
      console.warn('Failed to mark release note as seen:', error.response?.data?.message || error.message);
    }
  }, []);

  const handleNext = async () => {
    if (!activeNote?._id) {
      setOpen(false);
      return;
    }

    setMarking(true);
    await markSeen(activeNote._id);
    setMarking(false);

    if (activeIndex < notes.length - 1) {
      setActiveIndex((prev) => prev + 1);
      return;
    }

    setOpen(false);
    setNotes([]);
    setActiveIndex(0);
  };

  if (!activeNote) return null;

  return (
    <Dialog
      open={open}
      onClose={handleNext}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
          boxShadow: fullScreen ? 'none' : `0 28px 80px ${alpha('#0f172a', 0.28)}`,
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2.25, sm: 3 },
          pb: { xs: 2, sm: 2.5 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 48%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              bgcolor: '#0f172a',
              boxShadow: `0 16px 34px ${alpha('#0f172a', 0.24)}`,
              flexShrink: 0,
            }}
          >
            <CampaignRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 900, letterSpacing: 0.8 }}>
              New system update
            </Typography>
            <DialogTitle sx={{ p: 0, color: 'text.primary', fontWeight: 900, lineHeight: 1.15, mt: 0.25 }}>
              {activeNote.title}
            </DialogTitle>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {activeNote.version && <Chip size="small" label={`Version ${activeNote.version}`} />}
              {activeNote.startsAt && <Chip size="small" label={formatDate(activeNote.startsAt)} />}
              {notes.length > 1 && <Chip size="small" label={`${activeIndex + 1} of ${notes.length}`} />}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Stack spacing={2.25}>
          {activeNote.summary && (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={850} sx={{ lineHeight: 1.55 }}>
              {activeNote.summary}
            </Typography>
            </Box>
          )}

          {activeNote.details && (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.action.hover, 0.55),
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line', lineHeight: 1.75 }}
              >
                {activeNote.details}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: { xs: 2.25, sm: 3 },
          py: 2,
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          {notes.map((note, index) => (
            <Box
              key={note._id || index}
              sx={{
                width: index === activeIndex ? 18 : 7,
                height: 7,
                borderRadius: 999,
                bgcolor: index === activeIndex ? 'primary.main' : alpha(theme.palette.text.secondary, 0.22),
                transition: 'all 180ms ease',
              }}
            />
          ))}
        </Stack>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={marking}
          fullWidth={fullScreen}
          sx={{
            minWidth: fullScreen ? 'auto' : 150,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 900,
            bgcolor: '#0f172a',
            '&:hover': { bgcolor: '#111827' },
          }}
        >
          {activeIndex < notes.length - 1 ? 'Next update' : 'Got it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
